import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';
import { Form, Button, Container, Alert, Spinner, Card } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

const ForgotPassword = () => {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null); // State for reCAPTCHA token

  // Create a ref for the first input field
  const usernameRef = useRef(null);

  // Focus the first input field when the component mounts
  useEffect(() => {
    usernameRef.current.focus();
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setMessage('');

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/api/forgot-password', { username, email, recaptchaToken });
      setMessage(response.data.message);
    } catch (error) {
      console.error('Error resetting password:', error);

      if (error.response && error.response.data && error.response.data.message) {
        setError(error.response.data.message);
      } else {
        setError('Failed to send temporary password. Please check your username and email.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Basic front-end email validation
  const isEmailValid = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onReCAPTCHAChange = (token) => {
    setRecaptchaToken(token); // Set the reCAPTCHA token
  };

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6">
          <Card>
            <Card.Header>
              <h3 className="text-center">Forgot Password</h3>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              {message && <Alert variant="success">{message}</Alert>}
              <Form onSubmit={handleForgotPassword}>
                <Form.Group controlId="formUsername" className="mb-3">
                  <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                    placeholder="Enter your username"
                    disabled={isLoading}
                    ref={usernameRef} // Attach the ref to the input field
                  />
                </Form.Group>
                <Form.Group controlId="formEmail" className="mb-3">
                  <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                    isInvalid={!isEmailValid(email) && email !== ''}
                    disabled={isLoading}
                  />
                  <Form.Control.Feedback type="invalid">
                    Please provide a valid email address.
                  </Form.Control.Feedback>
                </Form.Group>
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} // Use your reCAPTCHA site key
                  onChange={onReCAPTCHAChange}
                />
                <Button
                  variant="primary"
                  type="submit"
                  className="w-100 mt-3"
                  disabled={isLoading || !isEmailValid(email)}
                >
                  {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Send Temporary Password'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
          <p className="mt-3 text-center">
            Remembered your password? <a href="/login">Login here</a>
          </p>
        </div>
      </div>
    </Container>
  );
};

export default ForgotPassword;